import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { SnackbarProvider } from "notistack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getAccountCloudType } from "../../CloudProviderContext";
import AdditionalAddons from "./AddonsManagement/AdditionalAddons/AdditionalAddons";
import ComplianceAddons from "./AddonsManagement/ComplianceAddons/ComplianceAddons";
import UpdateAccount from "./UpdateAccount";
import ViewAccountList from "./ViewAccountList";
import UpdateIamRole from "./UpdateIamRole";
import { shouldFilterUsername } from "../../helpers/util_helper";
import { AccountDetailThemeProvider } from "./accountDetailTheme";
import { ACCOUNT_DETAIL_FONT_SX } from "./accountDetailTypography";
import RemediationPreviewPanel, {
  isHttpUrlString,
  REMEDIATION_PREVIEW_PANEL_WIDTH_PX,
} from "../Common/RemediationPreviewPanel";

const TAB_ACTIVE_ICON_SX = {
  fontSize: 14,
  color: "#22c55e",
};

const TAB_INACTIVE_DOT_SX = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  flexShrink: 0,
  backgroundColor: "#F98125",
};

const EditAccountDialog = ({
  open,
  onClose,
  editAccount,
  accounts = [],
  complianceAddOns = [],
  addOns = [],
  externalId,
  updateData,
}) => {
  const isAzureAccount = getAccountCloudType(editAccount) === "AZURE";
  const [activeTab, setActiveTab] = useState(isAzureAccount ? "compliance" : "finops");
  // Survives Cloud Event / FinOps / Cost Optimization tab remounts (same unlock gate as Add Ons List).
  const [addonsWithBlueButtonClicked, setAddonsWithBlueButtonClicked] = useState(
    () => new Set()
  );
  const [guidePreviewUrl, setGuidePreviewUrl] = useState(null);

  const markBlueButtonClicked = (addonName) => {
    setAddonsWithBlueButtonClicked((prev) => new Set([...prev, addonName]));
  };

  const openGuidePreview = (url) => {
    if (isHttpUrlString(url)) setGuidePreviewUrl(String(url).trim());
  };

  const closeGuidePreview = () => setGuidePreviewUrl(null);

  useEffect(() => {
    setGuidePreviewUrl(null);
    if (!open) return;
    setActiveTab(isAzureAccount ? "compliance" : "finops");
    setAddonsWithBlueButtonClicked(new Set());
  }, [open, isAzureAccount, editAccount?.accountCode]);

  const filteredUsers = useMemo(() => {
    if (!editAccount?.listusr) return [];
    return editAccount.listusr.filter(
      (user) =>
        user.isActivated &&
        !shouldFilterUsername(user.userName)
    );
  }, [editAccount]);

  const tabs = isAzureAccount
    ? [
        { id: "compliance", label: "Compliance" },
        { id: "users", label: "Users" },
        { id: "resourceGroup", label: "Resource Group" },
      ]
    : [
        { id: "finops", label: "FinOps" },
        { id: "costOptimization", label: "Cost Optimization" },
        { id: "cloudEvent", label: "Cloud Event" },
        { id: "compliance", label: "Compliance" },
        { id: "users", label: "Users" },
        { id: "resourceGroup", label: "Resource Group" },
        { id: "pairing", label: "IAM" },
      ];

  if (!editAccount) return null;

  const isAddonActive = (addonName) =>
    (addOns || []).some((addon) => addon.name === addonName && addon.enabled);

  const activeTabStatus = {
    finops: isAddonActive("Cost Insight"),
    costOptimization: isAddonActive("Cost Optimization"),
    cloudEvent: isAddonActive("Event Insight"),
    compliance: (complianceAddOns || editAccount.complianceAddOns || []).some(
      (addon) => addon.enabled
    ),
  };

  const accountNumberLabel = isAzureAccount
    ? "Subscription ID"
    : "Account Number";

  return (
    <AccountDetailThemeProvider>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus={!!guidePreviewUrl}
      disableEscapeKeyDown={!!guidePreviewUrl}
      sx={
        guidePreviewUrl
          ? {
              "& .MuiDialog-container": {
                justifyContent: "center",
                overflowX: "hidden",
              },
            }
          : undefined
      }
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...(guidePreviewUrl
            ? { mr: { xs: 0, md: `${24 + REMEDIATION_PREVIEW_PANEL_WIDTH_PX}px` } }
            : {}),
          ...ACCOUNT_DETAIL_FONT_SX,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: "#F98125",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.35rem",
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Edit Account
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              color: "rgba(255,255,255,0.9)",
              fontSize: "0.875rem",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 2.5,
              rowGap: 0.5,
            }}
          >
            <Box component="span">
              {accountNumberLabel}:{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                {editAccount.accountNumber}
              </Box>
            </Box>
            <Box component="span">
              Account Name:{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                {editAccount.accountName}
              </Box>
            </Box>
          </Typography>
        </Box>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            padding: 0,
            margin: 0,
            border: "none",
            background: "transparent",
            color: "#FFFFFF",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            opacity: 1,
            boxShadow: "none",
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          width: "100%",
          px: 3,
          pt: 1.5,
          pb: 0,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f3f4f6",
            borderRadius: "10px",
            px: 0.5,
            py: 0.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              width: "100%",
              "& .MuiTabs-flexContainer": {
                width: "100%",
                alignItems: "center",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 40,
                minWidth: 0,
                flex: 1,
                maxWidth: "none",
                px: 0.5,
                fontSize: "0.8125rem",
                color: "#6b7280",
                fontWeight: 500,
                borderRadius: "8px",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#111827 !important",
                fontWeight: 700,
                backgroundColor: "#ffffff",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#F98125",
                height: 3,
                borderRadius: "2px 2px 0 0",
              },
            }}
          >
            {tabs.map((tab) => {
              const hasStatus = tab.id in activeTabStatus;
              const isActive = hasStatus && activeTabStatus[tab.id];
              return (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  label={
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: 0.75,
                      }}
                    >
                      {tab.label}
                      {hasStatus &&
                        (isActive ? (
                          <CheckCircleIcon sx={TAB_ACTIVE_ICON_SX} />
                        ) : (
                          <Box component="span" sx={TAB_INACTIVE_DOT_SX} />
                        ))}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Box>
      </Box>

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          px: 3,
          py: 2.5,
          overflowY: "auto",
          ...ACCOUNT_DETAIL_FONT_SX,
        }}
      >
        <SnackbarProvider>
          {activeTab === "finops" && (
            <AdditionalAddons
              addonName="Cost Insight"
              addOns={addOns}
              editAccount={editAccount}
              accounts={accounts}
              addonsWithBlueButtonClicked={addonsWithBlueButtonClicked}
              markBlueButtonClicked={markBlueButtonClicked}
              onOpenGuidePreview={openGuidePreview}
            />
          )}
          {activeTab === "costOptimization" && (
            <AdditionalAddons
              addonName="Cost Optimization"
              addOns={addOns}
              editAccount={editAccount}
              accounts={accounts}
              addonsWithBlueButtonClicked={addonsWithBlueButtonClicked}
              markBlueButtonClicked={markBlueButtonClicked}
              onOpenGuidePreview={openGuidePreview}
            />
          )}
          {activeTab === "cloudEvent" && (
            <AdditionalAddons
              addonName="Event Insight"
              addOns={addOns}
              editAccount={editAccount}
              accounts={accounts}
              addonsWithBlueButtonClicked={addonsWithBlueButtonClicked}
              markBlueButtonClicked={markBlueButtonClicked}
              onOpenGuidePreview={openGuidePreview}
            />
          )}
          {activeTab === "compliance" && (
            <ComplianceAddons
              complianceAddOns={complianceAddOns}
              editAccount={editAccount}
              isAzureAccount={isAzureAccount}
            />
          )}
          {activeTab === "users" && (
            <ViewAccountList
              embedded
              modal_static
              closeModal={onClose}
              accountCode={editAccount.accountCode}
              accountNumber={editAccount.accountNumber}
              userList={filteredUsers}
            />
          )}
          {activeTab === "resourceGroup" && (
            <UpdateAccount
              embedded
              selectedAccount={editAccount}
              closeModal={onClose}
              updateData={updateData}
            />
          )}
          {activeTab === "pairing" && (
            <UpdateIamRole
              externalId={externalId}
              selectedAccount={editAccount}
              addOns={addOns}
              closeModal={onClose}
              onOpenGuidePreview={openGuidePreview}
            />
          )}
        </SnackbarProvider>
      </DialogContent>
    </Dialog>
    <RemediationPreviewPanel
      url={guidePreviewUrl}
      onClose={closeGuidePreview}
      title="Guide"
    />
    </AccountDetailThemeProvider>
  );
};

export default EditAccountDialog;
